import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { cn } from '../lib/utils';
import { AppStoreButton, TireMark } from './shared/AppStoreButton';

interface LayoutProps {
  children: ReactNode;
}

// Marketing pages lay out their own full-width sections; the tool and legal
// pages get the standard content container.
const FULL_BLEED = ['/', '/how-it-works', '/tips', '/dashboard'];

const NAV = [
  { to: '/how-it-works', label: 'How it works', hint: 'Five minutes, step by step' },
  { to: '/tips', label: 'Tire tips', hint: 'Tread, sidewall, sizes' },
  { to: '/inspect', label: 'Check tires online', hint: 'Upload a photo' },
  { to: '/inspections', label: 'My inspections', hint: 'Your saved reports' },
];

/** Menu button drawn as a tire: it turns a quarter and the hub becomes a close mark when open. */
const TireMenuIcon = ({ open }: { open: boolean }) => (
  <svg viewBox="0 0 40 40" className="w-9 h-9" aria-hidden="true">
    <g style={{ transform: `rotate(${open ? 90 : 0}deg)`, transformOrigin: '20px 20px', transition: 'transform .45s cubic-bezier(.3,.7,.2,1)' }}>
      <circle cx="20" cy="20" r="19" fill="#202126" stroke={open ? 'rgba(255,255,255,.28)' : 'none'} strokeWidth="1" />
      {Array.from({ length: 16 }, (_, i) => (
        <rect key={i} x="18.6" y="1.8" width="2.8" height="3.6" rx="0.8" fill="#3A3B41" transform={`rotate(${i * 22.5} 20 20)`} />
      ))}
      <circle cx="20" cy="20" r="11" fill="#E9EAEE" />
    </g>
    {open ? (
      <path d="M16 16l8 8M24 16l-8 8" stroke="#2B63E6" strokeWidth="2.4" strokeLinecap="round" />
    ) : (
      <path d="M15 17h10M15 20h10M15 23h10" stroke="#46464F" strokeWidth="2" strokeLinecap="round" />
    )}
  </svg>
);

export const Layout = ({ children }: LayoutProps) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Close on navigation and Escape; keep the page behind from scrolling while open.
  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Tint Safari's status-bar area to match the dark panel.
    const meta = document.querySelector('meta[name="theme-color"]') ?? Object.assign(document.head.appendChild(document.createElement('meta')), { name: 'theme-color' });
    const prevTheme = meta.getAttribute('content');
    meta.setAttribute('content', '#202126');
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      if (prevTheme) meta.setAttribute('content', prevTheme);
      else meta.remove();
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header
        className={cn(
          'sticky top-0 z-50 backdrop-blur border-b no-print transition-colors duration-300',
          open ? 'bg-rubber text-white border-white/10 md:bg-white/90 md:text-ink md:border-line/60' : 'bg-white/90 border-line/60'
        )}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-2.5 shrink-0" onClick={() => setOpen(false)}>
            <TireMark className={cn('w-8 h-8 rounded-full transition-shadow', open && 'shadow-[0_0_0_1px_rgba(255,255,255,.28)] md:shadow-none')} />
            <span className="text-[19px] font-semibold tracking-tight" style={{ fontVariationSettings: '"wdth" 112' }}>
              Tread AI
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1" aria-label="Main">
            {NAV.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  cn(
                    'px-3 py-2 rounded-full text-[15px] transition-colors',
                    isActive ? 'bg-brand-100 text-brand-900' : 'text-muted hover:text-ink hover:bg-surface'
                  )
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:block">
            <AppStoreButton compact placement="header" />
          </div>

          <button
            type="button"
            className="md:hidden relative z-[60] -mr-1.5 p-1 rounded-full"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            aria-controls="mobile-menu"
            onClick={() => setOpen((v) => !v)}
          >
            <TireMenuIcon open={open} />
          </button>
        </div>
      </header>

      {/* Phone menu: a full-screen rubber panel with tread grooves down one edge */}
      <div
        id="mobile-menu"
        className={cn(
          'md:hidden fixed inset-0 z-40 bg-rubber text-white transition-[opacity,transform] duration-300 ease-out no-print',
          open ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-3 pointer-events-none'
        )}
        style={{ height: '100dvh' }}
        aria-hidden={!open}
        inert={!open}
      >
        <svg aria-hidden="true" className="absolute inset-y-0 right-0 h-full w-24 opacity-[0.08]" preserveAspectRatio="none" viewBox="0 0 40 100">
          {[6, 20, 34].map((x) => (
            <path key={x} d={`M${x} 0 l4 6 -4 6 4 6 -4 6 4 6 -4 6 4 6 -4 6 4 6 -4 6 4 6 -4 6 4 6 -4 6 4 6 -4 6 4 6`} stroke="#fff" strokeWidth="2" fill="none" vectorEffect="non-scaling-stroke" />
          ))}
        </svg>
        <nav
          className="relative h-full flex flex-col px-6"
          style={{ paddingTop: 'calc(env(safe-area-inset-top) + 6rem)', paddingBottom: 'calc(env(safe-area-inset-bottom) + 2rem)' }}
          aria-label="Main"
        >
          <ul className="space-y-1">
            {NAV.map((l, i) => {
              const active = location.pathname === l.to;
              return (
                <li
                  key={l.to}
                  className="transition-[opacity,transform] duration-500"
                  style={{ transitionDelay: open ? `${80 + i * 50}ms` : '0ms', opacity: open ? 1 : 0, transform: open ? 'none' : 'translateY(10px)' }}
                >
                  <Link to={l.to} onClick={() => setOpen(false)} className="group flex items-center gap-4 py-4 border-b border-white/10" aria-current={active ? 'page' : undefined}>
                    <span className={cn('h-8 w-3 rounded-[4px] shrink-0 transition-colors', active ? 'bg-brand-500' : 'bg-white/15 group-active:bg-white/30')} aria-hidden="true" />
                    <span className="flex-1">
                      <span className="block text-[1.75rem] leading-tight font-semibold tracking-tight" style={{ fontVariationSettings: '"wdth" 115' }}>
                        {l.label}
                      </span>
                      <span className="block text-sm text-white/50 mt-0.5">{l.hint}</span>
                    </span>
                    {active && <span className="h-2.5 w-2.5 rounded-full bg-brand-500" aria-hidden="true" />}
                  </Link>
                </li>
              );
            })}
          </ul>
          <div className="mt-auto">
            <p className="text-white/60 text-sm mb-4">First inspection free. About five minutes for all four tires.</p>
            <AppStoreButton placement="mobile_menu" className="ring-1 ring-white/25 rounded-xl" />
          </div>
        </nav>
      </div>

      <main className="flex-1">
        {FULL_BLEED.includes(location.pathname) ? (
          children
        ) : (
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 md:py-12">{children}</div>
        )}
      </main>

      <footer className="border-t border-line/60 bg-surface no-print">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 grid gap-10 md:grid-cols-[1.4fr_1fr_1fr]">
          <div className="max-w-sm">
            <Link to="/" className="flex items-center gap-2.5">
              <TireMark className="w-7 h-7" />
              <span className="font-semibold tracking-tight">Tread AI</span>
            </Link>
            <p className="mt-3 text-[15px] text-muted leading-relaxed">
              Check your tires with your phone between professional inspections. Built by a small team of software and
              machine-learning engineers in California.
            </p>
            <AppStoreButton compact className="mt-5" placement="footer" />
          </div>

          <div>
            <h2 className="text-sm font-semibold text-ink">Product</h2>
            <ul className="mt-3 space-y-2 text-[15px] text-muted">
              <li><Link className="hover:text-ink" to="/how-it-works">How it works</Link></li>
              <li><Link className="hover:text-ink" to="/tips">Tire tips</Link></li>
              <li><Link className="hover:text-ink" to="/inspect">Check tires online</Link></li>
              <li><Link className="hover:text-ink" to="/api">Developer API</Link></li>
            </ul>
          </div>

          <div>
            <h2 className="text-sm font-semibold text-ink">Contact</h2>
            <ul className="mt-3 space-y-2 text-[15px] text-muted">
              <li><a className="hover:text-ink" href="mailto:contact@tread-ai.com">contact@tread-ai.com</a></li>
              <li><a className="hover:text-ink" href="mailto:sizarta@gmail.com">sizarta@gmail.com</a></li>
              <li><a className="hover:text-ink tabular" href="tel:+19495584019">(949) 558-4019</a></li>
              <li><Link className="hover:text-ink" to="/terms">Terms of service</Link></li>
              <li><Link className="hover:text-ink" to="/privacy">Privacy policy</Link></li>
            </ul>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-8 text-sm text-muted">
          © {new Date().getFullYear()} Tread AI. Readings are estimates; always have a professional confirm before replacing tires.
        </div>
      </footer>
    </div>
  );
};
